import { useEffect, useRef, useState, type ReactNode } from 'react';
import { IconCheck, IconMinus } from '@tabler/icons-react';
import { allState, toggleAll, toggleOne } from './selection';
import { virtualWindow } from './virtual';
import { useT } from '@hana/i18n';
import s from './kit.module.css';

export interface Column<R> {
  key: string;
  header: ReactNode;
  /** độ rộng cố định (cột bảng không tràn — Hybrid-Kit §6) */
  width?: number | string;
  align?: 'left' | 'right';
  /** cột phụ: ẩn trên điện thoại (< 768px) để bảng không phải cuộn ngang */
  minor?: boolean;
  cell: (row: R) => ReactNode;
}

/** Bảng: tiêu đề cột dính khi cuộn, chọn dòng (ô đầu), mật độ Chuẩn 48 / Gọn 40. */
export function Table<R>({
  columns,
  rows,
  rowKey,
  label,
  selected,
  onSelect,
  onRowClick,
  dense,
  maxHeight,
  rowHeight,
  selectAllLabel,
  selectLabel,
}: {
  columns: readonly Column<R>[];
  rows: readonly R[];
  rowKey: (r: R) => string;
  label: string;
  selected?: ReadonlySet<string>;
  onSelect?: (next: Set<string>) => void;
  onRowClick?: (r: R) => void;
  dense?: boolean;
  maxHeight?: number | string;
  /** chiều cao cố định mỗi dòng (px): bật cuộn ảo — cần `maxHeight` dạng số; dòng nhiều dòng chữ dùng giá trị này */
  rowHeight?: number;
  /** nhãn ô chọn tất cả (mặc định ui.selectAll) */
  selectAllLabel?: string;
  /** nhãn ô chọn từng dòng theo khoá dòng (mặc định ui.select) */
  selectLabel?: (key: string) => string;
}) {
  const tr = useT();
  const keys = rows.map(rowKey);
  const sel = selected ?? new Set<string>();
  const st = allState(sel, keys);
  const wrap = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ top: 0, h: typeof maxHeight === 'number' ? maxHeight : 600 });
  useEffect(() => {
    const el = wrap.current;
    if (!rowHeight || !el) return;
    const read = () => setView((v) => (v.h === el.clientHeight || !el.clientHeight ? v : { ...v, h: el.clientHeight }));
    read();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read);
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [rowHeight]);
  const win = rowHeight ? virtualWindow(rows.length, rowHeight, view.top, view.h) : { start: 0, end: rows.length, padTop: 0, padBottom: 0 };
  const span = columns.length + (onSelect ? 1 : 0);

  return (
    <div
      ref={wrap}
      className={s.tableWrap}
      style={{ maxHeight }}
      onScroll={rowHeight ? (e) => setView({ top: e.currentTarget.scrollTop, h: e.currentTarget.clientHeight || view.h }) : undefined}
    >
      <table className={`${s.table} ${dense ? s.dense : ''}`} aria-label={label} aria-rowcount={rowHeight ? rows.length + 1 : undefined}>
        <thead>
          <tr>
            {onSelect && (
              <th className={s.sel}>
                <CheckBtn state={st} label={selectAllLabel ?? tr('ui.selectAll')} onToggle={() => onSelect(toggleAll(sel, keys))} />
              </th>
            )}
            {columns.map((c) => (
              <th key={c.key} scope="col" className={cellCls(c)} style={{ width: c.width }}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {win.padTop > 0 && <Spacer h={win.padTop} span={span} />}
          {rows.slice(win.start, win.end).map((r, j) => {
            const i = win.start + j;
            const k = keys[i] as string;
            const on = sel.has(k);
            const cls = [on && s.rowOn, onRowClick && s.clickable].filter(Boolean).join(' ') || undefined;
            return (
              <tr
                key={k}
                className={cls}
                style={rowHeight ? { height: rowHeight } : undefined}
                aria-rowindex={rowHeight ? i + 2 : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          onRowClick(r);
                        }
                      }
                    : undefined
                }
              >
                {onSelect && (
                  <td className={s.sel} onClick={(e) => e.stopPropagation()}>
                    <CheckBtn state={on ? 'all' : 'none'} label={selectLabel ? selectLabel(k) : tr('ui.select', { k })} onToggle={() => onSelect(toggleOne(sel, k))} />
                  </td>
                )}
                {columns.map((c) => (
                  <td key={c.key} className={cellCls(c)}>
                    {c.cell(r)}
                  </td>
                ))}
              </tr>
            );
          })}
          {win.padBottom > 0 && <Spacer h={win.padBottom} span={span} />}
        </tbody>
      </table>
    </div>
  );
}

/** Ô chọn dạng nút role=checkbox 36×36 (hộp 18, mẫu: Mint-TMS-Routes). */
function CheckBtn({ state, label, onToggle }: { state: 'all' | 'some' | 'none'; label: string; onToggle: () => void }) {
  return (
    <button type="button" role="checkbox" className={s.checkBtn} aria-checked={state === 'all' ? true : state === 'some' ? 'mixed' : false} aria-label={label} onClick={onToggle}>
      <span className={`${s.checkBox} ${state !== 'none' ? s.checkBoxOn : ''}`} aria-hidden="true">
        {state === 'all' && <IconCheck size={12} stroke={3} />}
        {state === 'some' && <IconMinus size={12} stroke={3} />}
      </span>
    </button>
  );
}

function Spacer({ h, span }: { h: number; span: number }) {
  return (
    <tr aria-hidden="true" className={s.spacer} style={{ height: h }}>
      <td colSpan={span} />
    </tr>
  );
}

function cellCls(c: Column<never>): string | undefined {
  return [c.align === 'right' && s.num, c.minor && s.minor].filter(Boolean).join(' ') || undefined;
}

/** Thanh thao tác hàng loạt khi đã chọn dòng (nền xanh nhạt, không dải tối). */
export function SelectionBar({ count, noun, children, onClear }: { count: number; noun: string; children?: ReactNode; onClear: () => void }) {
  const tr = useT();
  if (count === 0) return null;
  return (
    <div role="toolbar" aria-label={tr('ui.bulk')} className={s.bulk}>
      <b>
        {tr('ui.selected', { n: count, noun })}
      </b>
      <div className={s.bulkActs}>
        {children}
        <button type="button" className={`${s.btn} ${s.sm} ${s.ghost}`} style={{ color: 'var(--link)' }} onClick={onClear}>
          {tr('ui.clearSel')}
        </button>
      </div>
    </div>
  );
}
